import React, { useRef, memo, useCallback, useMemo } from 'react';

import { FileItem } from '../../types';

interface FileUploadProps {
    files: FileItem[];
    onFileSelect: (files: File[]) => void;
    onRemoveFile: (id: number) => void;
    onClearFiles: () => void;
}

const FileUpload: React.FC<FileUploadProps> = memo(({
    files,
    onFileSelect,
    onRemoveFile,
    onClearFiles: _onClearFiles
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        onFileSelect(selectedFiles);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [onFileSelect]);

    // const handleUploadClick = useCallback(() => {
    //     fileInputRef.current?.click();
    // }, []);

    const formatFileSize = useCallback((bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    const handleRemoveFile = useCallback((id: number) => {
        onRemoveFile(id);
    }, [onRemoveFile]);

    // 点击待上传文件 → 预览（与离线版一致：window.open 新窗口打开）
    const previewFile = useCallback((file: FileItem) => {
        const url = file.preview || URL.createObjectURL(file.file);
        window.open(url, '_blank');
    }, []);

    // 优化文件列表渲染
    const filesList = useMemo(() => {
        if (files.length === 0) return null;

        // 2026-09-18 用户令：对齐生产链输入框底部紧凑 chips——
        // 图片小缩略图（点击放大核对）、📄 图标、文件名+KB、✕ 删除重传，无头行
        return (
            <div className="files-chips">
                {files.map(file => (
                    <span key={file.id} className="pf-chip">
                        {file.preview
                            ? <img className="pf-thumb" src={file.preview} alt={file.name}
                                   title={`${file.name}（点击放大核对）`}
                                   onClick={() => previewFile(file)} />
                            : <span className="pf-icon">📄</span>}
                        <span className="pf-name" title={file.name}>{file.name}</span>
                        <span className="pf-size">({formatFileSize(file.size)})</span>
                        <span className="pf-remove" title="移除"
                              onClick={() => handleRemoveFile(file.id)}>✕</span>
                    </span>
                ))}
            </div>
        );
    }, [files, handleRemoveFile, previewFile, formatFileSize]);

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                style={{ display: 'none' }}
                // 与后端 FileExtensionValidator 白名单对齐（jpg/jpeg/png/tif/tiff/pdf/docx/zip）
                // 友好性⑨：zip 单件后端解压展开为合并案例
                accept="image/*,.tif,.tiff,.pdf,.docx,.zip"
            />

            {filesList}
        </>
    );
});


FileUpload.displayName = 'FileUpload';

export default FileUpload;
